import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { UserOnboardingStateView } from '@reflex/shared';

/**
 * Fetches the user's onboarding state from the server.
 * Returns step statuses (completed/skipped/incomplete), progress,
 * and whether any outstanding steps remain.
 */
export function useUserOnboardingState() {
  return useQuery({
    queryKey: queryKeys.onboardingState,
    queryFn: () => api.getOnboardingState(),
  });
}

/**
 * Mutation hook to skip an onboarding step.
 * Updates the query cache on success so the UI reflects the change immediately.
 */
export function useUserOnboardingSkip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stepId: string) => api.skipOnboardingStep(stepId),
    onSuccess: (updatedState) => {
      queryClient.setQueryData<UserOnboardingStateView>(queryKeys.onboardingState, updatedState);
    },
  });
}

/**
 * Mutation hook to dismiss the onboarding wizard.
 *
 * Sets `firstCompletedAt` to mark that the user has clicked "Continue to Sessions"
 * at least once. Outstanding steps continue to surface via the home banner.
 *
 * Stores the server's authoritative `UserOnboardingStateView` response in
 * the cache (rather than patching `firstCompletedAt` locally) so step
 * status, progress, and banner visibility all stay in lockstep with the
 * server. The server also mirrors the dismissal onto
 * `user.onboardingCompletedAt`, which lives in `useAuth`'s reducer rather
 * than a React Query cache; nothing in the web app reads it, so this hook
 * deliberately leaves the session alone rather than forcing a re-fetch.
 */
export function useUserOnboardingDismiss() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.dismissOnboardingWizard(),
    onSuccess: (updatedState) => {
      queryClient.setQueryData<UserOnboardingStateView>(queryKeys.onboardingState, updatedState);
    },
  });
}

/**
 * Mutation hook to dismiss the required-configuration banner.
 *
 * Stores the current requirements fingerprint so the banner stays hidden
 * until requirements change (new step, regressed step, status change).
 *
 * Updates the query cache on success so the UI reflects the change immediately.
 */
export function useUserOnboardingBannerDismiss() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.dismissOnboardingBanner(),
    onSuccess: (updatedState) => {
      queryClient.setQueryData<UserOnboardingStateView>(queryKeys.onboardingState, updatedState);
    },
  });
}
